import { useEffect, useRef, useState } from 'react';

import {
  listenRpaExecutorStatus,
  rpaBridge,
  type RpaExecutorStateSnapshot,
} from '@/tauri/rpa';

/**
 * Subscribe-then-read the executor snapshot for the cua-status window.
 *
 * Ordering matters: attach the listener FIRST, then hydrate via
 * `readRunExecutorState`, so an event landing between read and subscribe
 * is not lost. Keep the last non-null snapshot — never clear on a transient
 * null (terminal-linger readability is Rust's job via the lingering fallback).
 *
 * A slow hydration read that resolves after a live event must not rewind
 * state — ignore the read once any event has been applied.
 */
export const useExecutorSnapshot = (): RpaExecutorStateSnapshot | null => {
  const [snapshot, setSnapshot] = useState<RpaExecutorStateSnapshot | null>(null);
  const hasEventRef = useRef(false);

  useEffect(() => {
    if (!rpaBridge.isAvailable()) return;

    let cancelled = false;
    let unlisten: (() => void) | undefined;
    hasEventRef.current = false;

    const attach = async () => {
      unlisten = await listenRpaExecutorStatus((event) => {
        if (cancelled) return;
        hasEventRef.current = true;
        setSnapshot(event.payload);
      });
      if (cancelled) {
        unlisten();
        return;
      }
      try {
        const current = await rpaBridge.readRunExecutorState();
        if (cancelled) return;
        // Ignore a stale hydration that lost the race to a live event.
        if (hasEventRef.current) return;
        if (current) setSnapshot(current);
      } catch {
        // Hydration failure is non-fatal — live events still arrive.
      }
    };

    void attach();

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);

  return snapshot;
};
